import { Geotab, LogRecord } from "@fairfleet/geotab";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { paginatedFeed } from "@/utilities/query/paginatedFeed";

/** How many records one plain `Get` may return when the feed is not available. */
const FALLBACK_RESULTS_LIMIT = 50_000;

/**
 * The positions one vehicle reported between two times.
 *
 * The feed is used because a day of driving is tens of thousands of records and the feed pages
 * through them. Not every database allows a feed for this type, though, and the ones that do not
 * answer with an "invalid operation" rather than an empty page — so a plain read is tried after
 * that, with an explicit limit.
 */
async function fetchLogRecords(
  geotabClient: Geotab,
  deviceId: string,
  fromDate: Date,
  toDate: Date,
  signal?: AbortSignal,
): Promise<LogRecord[]> {
  const search = { deviceSearch: { id: deviceId }, fromDate, toDate };

  try {
    return await paginatedFeed(geotabClient, "LogRecord", search, undefined, signal);
  } catch (error) {
    if (!isFeedUnsupported(error)) {
      throw error;
    }
    return (await geotabClient.get("LogRecord", search, FALLBACK_RESULTS_LIMIT, signal)) ?? [];
  }
}

function isFeedUnsupported(error: unknown): boolean {
  const geotabError = error as { code?: number; data?: { type?: string } } | null;
  return (
    geotabError?.code === -32000 || geotabError?.data?.type === "InvalidApiOperationException"
  );
}

/**
 * The positions of one vehicle, refreshed while the caller is watching.
 *
 * @param interval - How often to ask again. `false` asks once, which is what a caller looking at
 * a journey that has already happened wants — the answer cannot change.
 */
export function useLogRecords(
  deviceId: string | undefined,
  fromDate: Date,
  toDate: Date,
  interval?: number | false,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery<LogRecord[]>({
    queryKey: ["logRecords", activeSessionId, deviceId, fromDate, toDate],

    queryFn: ({ signal }) =>
      deviceId
        ? fetchLogRecords(geotabClient, deviceId, fromDate, toDate, signal)
        : Promise.resolve([]),

    refetchInterval: interval ?? 12000,
  });
}

/** One vehicle and the window to read for it. */
export interface DeviceLogRecordRange {
  deviceId: string;
  fromDate: Date;
  toDate: Date;
}

export interface LogRecordsForRanges {
  logRecords: LogRecord[];
  isLoading: boolean;
}

/**
 * The positions of several vehicles, each over its own window.
 *
 * A window per vehicle rather than one window spanning all of them: with one vehicle's journey on
 * Monday and another's on Friday, a shared window would read five days of positions for both.
 *
 * Asked once and not repeated. These are journeys that have already happened.
 *
 * @param ranges - One entry per vehicle. Must be stable between renders.
 */
export function useLogRecordsForDeviceRanges(
  ranges: readonly DeviceLogRecordRange[],
): LogRecordsForRanges {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQueries({
    queries: ranges.map(range => ({
      queryKey: [
        "logRecords",
        activeSessionId,
        range.deviceId,
        range.fromDate,
        range.toDate,
      ] as const,
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        fetchLogRecords(geotabClient, range.deviceId, range.fromDate, range.toDate, signal),
      refetchInterval: false as const,
      refetchOnWindowFocus: false,
    })),

    combine: results => ({
      logRecords: results.flatMap(result => result.data ?? []),
      isLoading: results.some(result => result.isLoading),
    }),
  });
}
